import React, { useState } from 'react';

// Score Card component for displaying PageSpeed scores
const ScoreCard = ({ title, score, color }) => {
  // Calculate the gradient color based on score
  const getColor = (score) => {
    if (score >= 90) return '#0cce6b'; // Good
    if (score >= 50) return '#ffa400'; // Needs Improvement
    return '#ff4e42'; // Poor
  };
  
  // Calculate stroke dash for circular progress
  const calculateStrokeDashoffset = (score) => {
    const circumference = 2 * Math.PI * 40; // r=40
    return circumference - (score / 100) * circumference;
  };
  
  return (
    <div style={{
      flex: '1 0 200px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      textAlign: 'center'
    }}>
      <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>{title}</h4>
      <div style={{ position: 'relative', width: '100px', height: '100px', margin: '0 auto' }}>
        <svg width="100" height="100" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="#efefef"
            strokeWidth="10"
          />
          {/* Score circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke={getColor(score)}
            strokeWidth="10"
            strokeDasharray={2 * Math.PI * 40}
            strokeDashoffset={calculateStrokeDashoffset(score)}
            transform="rotate(-90 50 50)"
            strokeLinecap="round"
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '24px',
          fontWeight: 'bold',
          color: getColor(score)
        }}>
          {Math.round(score)}
        </div>
      </div>
    </div>
  );
};

// Metric Card component for displaying performance metrics
const MetricCard = ({ name, value, score }) => {
  // Calculate the status color based on score
  const getStatusColor = (score) => {
    if (score >= 0.9) return '#0cce6b'; // Good
    if (score >= 0.5) return '#ffa400'; // Needs Improvement
    return '#ff4e42'; // Poor
  };
  
  return (
    <div style={{
      flex: '1 0 200px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderLeft: `4px solid ${getStatusColor(score)}`
    }}>
      <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>{name}</h5>
      <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0' }}>{value}</p>
      <div style={{
        marginTop: '5px',
        height: '4px',
        width: '100%',
        backgroundColor: '#e9ecef',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${score * 100}%`,
          backgroundColor: getStatusColor(score),
          borderRadius: '2px'
        }} />
      </div>
    </div>
  );
};

const Results = ({ data }) => {
  // State to track active tab for Lighthouse results
  const [activeTab, setActiveTab] = useState('mobile');
  if (!data) return null;

  return (
    <div className="results-container">
      <h2>Analysis Results for: {data.url}</h2>
      
      {/* Screenshot Display */}
      {data.screenshots && typeof data.screenshots.desktop === 'string' && data.screenshots.desktop.trim() !== '' && (
        <div className="result-section">
          <h3>Website Screenshot (Desktop)</h3>
          <div className="screenshot-container" style={{
            marginTop: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '4px', 
            overflow: 'hidden',
            /* Consider if maxHeight/overflowY is still needed if only one screenshot type shown */
          }}>
            <img
              src={`${process.env.REACT_APP_BROWSER_SERVICE_URL}${data.screenshots.desktop}`}
              alt="Desktop Website Screenshot"
              style={{ 
                width: '100%', 
                display: 'block',
                maxWidth: '100%',
                height: 'auto'
              }}
            />
          </div>
        </div>
      )}
      
      {/* WordPress Results */}
      <div className="result-section">
        <h3>WordPress Detection</h3>
        {data.wordpress.error ? (
          <div className="error-message">
            <p>{data.wordpress.error}</p>
          </div>
        ) : (
          <div>
            <p>
              <strong>WordPress Detected:</strong> {data.wordpress.is_wordpress ? 'Yes' : 'No'}
            </p>
            
            {data.wordpress.is_wordpress && (
              <>
                {data.wordpress.version && (
                  <p><strong>Version:</strong> {data.wordpress.version}</p>
                )}
                
                {/* Vulnerabilities */}
                {data.wordpress.vulnerabilities && data.wordpress.vulnerabilities.length > 0 ? (
                  <div className="vulnerabilities">
                    <h4>Vulnerabilities Found:</h4>
                    <ul className="vuln-list">
                      {data.wordpress.vulnerabilities.map((vuln, index) => (
                        <li key={index} className={`severity-${vuln.severity.toLowerCase()}`}>
                          <strong>{vuln.title}</strong>
                          <p>Fixed in version: {vuln.fixed_in}</p>
                          <p>Severity: {vuln.severity}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p>No WordPress core vulnerabilities found.</p>
                )}
                
                {/* Theme Information */}
                {data.wordpress.theme && data.wordpress.theme.name && (
                  <div className="theme-info" style={{marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                    <h4>WordPress Theme</h4>
                    <p style={{margin: '5px 0'}}><strong>Name:</strong> {data.wordpress.theme.name}</p>
                    {data.wordpress.theme.version && (
                      <p style={{margin: '5px 0'}}><strong>Version:</strong> {data.wordpress.theme.version}</p>
                    )}
                    {data.wordpress.theme.author && (
                      <p style={{margin: '5px 0'}}><strong>Author:</strong> {data.wordpress.theme.author}</p>
                    )}
                    {data.wordpress.theme.theme_uri && (
                      <p style={{margin: '5px 0'}}>
                        <strong>Theme URI:</strong> <a href={data.wordpress.theme.theme_uri} target="_blank" rel="noopener noreferrer">
                          {data.wordpress.theme.theme_uri}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                {/* Plugins */}
                {data.wordpress.plugins && data.wordpress.plugins.length > 0 ? (
                  <div className="plugins">
                    <h4>Detected Plugins:</h4>
                    <ul>
                      {data.wordpress.plugins.map((plugin, index) => (
                        <li key={index}>
                          {plugin.name}
                          {plugin.version && <span> (Version: {plugin.version})</span>}
                          {plugin.vulnerabilities && plugin.vulnerabilities.length > 0 && (
                            <span className="warning-text"> - Vulnerabilities found!</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p>No WordPress plugins detected.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* SSL Certificate Results */}
      <div className="result-section">
        <h3>SSL Certificate</h3>
        <div>
          <p><strong>Has SSL:</strong> {data.ssl.has_ssl ? 'Yes' : 'No'}</p>
          
          {data.ssl.has_ssl && (
            <>
              {data.ssl.corporate_proxy_detected && (
                <div className="warning-box" style={{background: '#fffbea', border: '1px solid #ffd700', padding: '10px', margin: '10px 0', borderRadius: '4px'}}>
                  <p style={{color: '#856404', fontWeight: 'bold', marginBottom: '5px'}}>
                    ⚠️ Corporate Security Proxy Detected
                  </p>
                  <p style={{fontSize: '0.9em', marginBottom: '0'}}>
                    The SSL certificate information shown below is from your corporate network security proxy ({data.ssl.issuer}),
                    not the actual website's certificate. This is normal on corporate networks that monitor internet traffic.
                  </p>
                </div>
              )}
              
              <p><strong>Valid:</strong> {data.ssl.valid ? 'Yes' : 'No'}</p>
              {data.ssl.issuer && (
                <p>
                  <strong>Issuer:</strong> {data.ssl.issuer}
                  {data.ssl.true_issuer_unknown && (
                    <span style={{color: '#856404', fontSize: '0.9em', marginLeft: '5px'}}>
                      (corporate certificate, not actual website certificate)
                    </span>
                  )}
                </p>
              )}
              {data.ssl.expiry_date && <p><strong>Expiry Date:</strong> {data.ssl.expiry_date}</p>}
              {data.ssl.days_remaining !== null && (
                <p><strong>Days Remaining:</strong> {data.ssl.days_remaining}</p>
              )}
            </>
          )}
          
          {data.ssl.issues && data.ssl.issues.length > 0 && (
            <div className="issues">
              <h4>SSL Issues:</h4>
              <ul className="issue-list">
                {data.ssl.issues.map((issue, index) => (
                  <li key={index} className="issue-item">{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* Security Headers Results */}
      {data.security_headers && (
        <div className="result-section">
          <h3>HTTP Security Headers</h3>
          <p><strong>Security Score:</strong> <span className={`score-${Math.floor(data.security_headers.score / 25)}`} style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: data.security_headers.score >= 75 ? '#d4edda' :
                          data.security_headers.score >= 50 ? '#fff3cd' :
                          data.security_headers.score >= 25 ? '#f8d7da' : '#f8d7da',
            color: data.security_headers.score >= 75 ? '#155724' :
                  data.security_headers.score >= 50 ? '#856404' :
                  data.security_headers.score >= 25 ? '#721c24' : '#721c24'
          }}>{data.security_headers.score}%</span></p>

          {/* Display present security headers */}
          {data.security_headers.present_headers && data.security_headers.present_headers.length > 0 && (
            <div>
              <h4>Implemented Security Headers:</h4>
              <ul className="present-headers-list" style={{listStyleType: 'none', padding: 0}}>
                {data.security_headers.present_headers.map((header, index) => (
                  <li key={index} style={{
                    margin: '8px 0',
                    padding: '8px',
                    backgroundColor: '#e6f7ff',
                    borderLeft: '4px solid #1890ff'
                  }}>
                    <strong>{header.header}</strong>
                    <p style={{fontSize: '0.9em', marginTop: '4px', marginBottom: '0', wordBreak: 'break-all'}}>
                      <code>{header.value}</code>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.security_headers.missing_headers && data.security_headers.missing_headers.length > 0 && (
            <div>
              <h4>Missing Security Headers:</h4>
              <ul className="missing-headers-list" style={{listStyleType: 'none', padding: 0}}>
                {data.security_headers.missing_headers.map((header, index) => (
                  <li key={index} style={{
                    margin: '8px 0',
                    padding: '8px',
                    backgroundColor: header.importance === 'high' ? '#f8d7da' :
                                    header.importance === 'medium' ? '#fff3cd' : '#f2f2f2',
                    borderLeft: `4px solid ${header.importance === 'high' ? '#dc3545' :
                                            header.importance === 'medium' ? '#ffc107' : '#6c757d'}`
                  }}>
                    <strong>{header.header}</strong>: {header.description}
                    <p style={{fontSize: '0.9em', marginTop: '4px', marginBottom: '0'}}>
                      <em>Recommended: {header.recommended}</em>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.security_headers.issues && data.security_headers.issues.length > 0 && (
            <div>
              <h4>Security Issues:</h4>
              <ul className="issue-list" style={{listStyleType: 'none', padding: 0}}>
                {data.security_headers.issues.map((issue, index) => (
                  <li key={index} style={{margin: '8px 0', padding: '8px', backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545'}}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Show all headers for reference */}
          <details style={{marginTop: '15px'}}>
            <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>View All Headers</summary>
            <div style={{maxHeight: '200px', overflowY: 'auto', fontSize: '0.9em', fontFamily: 'monospace', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px'}}>
              {data.security_headers.headers && Object.keys(data.security_headers.headers).map((headerName) => (
                <div key={headerName} style={{marginBottom: '5px'}}>
                  <strong>{headerName}:</strong> {data.security_headers.headers[headerName]}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* Links Analysis Results */}
      {data.links && (
        <div className="result-section">
          <h3>Link Analysis</h3>
          
          <div className="stats-section" style={{marginBottom: '15px'}}>
            <p><strong>Pages Scanned:</strong> {data.links.total_pages_scanned || 0}</p>
            <p><strong>Links Checked:</strong> {data.links.total_links_checked || 0}</p>
            
            {data.links.total_broken > 0 ? (
              <p>
                <strong>Broken Links:</strong> <span style={{color: '#dc3545', fontWeight: 'bold'}}>
                  {data.links.total_broken} found
                </span>
              </p>
            ) : (
              <p>
                <strong>Broken Links:</strong> <span style={{color: '#28a745'}}>
                  None found
                </span>
              </p>
            )}
            
            {data.links.total_redirects > 0 && (
              <p>
                <strong>Redirects:</strong> {data.links.total_redirects} found
              </p>
            )}
            
            {data.links.mixed_content && data.links.mixed_content.has_mixed_content && (
              <p>
                <strong>Mixed Content:</strong> <span style={{color: '#dc3545', fontWeight: 'bold'}}>
                  Found insecure content on secure site
                </span>
              </p>
            )}
          </div>
          
          {/* Broken Links Section */}
          {data.links.broken_links && data.links.broken_links.length > 0 && (
            <div className="broken-links-section">
              <h4>Broken Links</h4>
              <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{backgroundColor: '#f8f9fa', textAlign: 'left'}}>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6'}}>URL</th>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6', width: '100px'}}>Type</th>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6', width: '100px'}}>Status</th>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6', width: '180px'}}>Found On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.links.broken_links.map((link, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #dee2e6'}}>
                        <td style={{padding: '8px', wordBreak: 'break-all'}}>
                          <code>{link.url}</code>
                        </td>
                        <td style={{padding: '8px'}}>
                          {link.type || 'LINK'}
                        </td>
                        <td style={{padding: '8px', color: '#dc3545'}}>
                          {link.status_code === 0 ? 'Connection Error' : `HTTP ${link.status_code}`}
                        </td>
                        <td style={{padding: '8px', fontSize: '0.85em'}}>
                          {link.found_on === 'direct-access' ? 'Direct URL' : 
                           <span style={{display: 'block', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={link.found_on}>
                             {link.found_on}
                           </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Mixed Content Section */}
          {data.links.mixed_content && data.links.mixed_content.has_mixed_content && (
            <div className="mixed-content-section">
              <h4>Mixed Content Issues</h4>
              <p style={{color: '#856404', backgroundColor: '#fff3cd', padding: '10px', borderRadius: '4px'}}>
                This HTTPS site is loading resources over insecure HTTP, which can lead to security vulnerabilities.
              </p>
              <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{backgroundColor: '#f8f9fa', textAlign: 'left'}}>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6'}}>Type</th>
                      <th style={{padding: '8px', borderBottom: '1px solid #dee2e6'}}>Resource</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.links.mixed_content.mixed_resources.map((resource, index) => (
                      <tr key={index} style={{borderBottom: '1px solid #dee2e6'}}>
                        <td style={{padding: '8px'}}>{resource.type}</td>
                        <td style={{padding: '8px', wordBreak: 'break-all'}}>
                          <code>{resource.url}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Redirects Section (collapsible) */}
          {data.links.redirects && data.links.redirects.length > 0 && (
            <div className="redirects-section" style={{marginTop: '15px'}}>
              <details>
                <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                  View {data.links.redirects.length} Redirects
                </summary>
                <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                      <tr style={{backgroundColor: '#f8f9fa', textAlign: 'left'}}>
                        <th style={{padding: '8px', borderBottom: '1px solid #dee2e6'}}>URL</th>
                        <th style={{padding: '8px', borderBottom: '1px solid #dee2e6', width: '100px'}}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.links.redirects.map((redirect, index) => (
                        <tr key={index} style={{borderBottom: '1px solid #dee2e6'}}>
                          <td style={{padding: '8px', wordBreak: 'break-all'}}>
                            <code>{redirect.url}</code>
                          </td>
                          <td style={{padding: '8px'}}>
                            HTTP {redirect.status_code}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Content Results */}
      <div className="result-section">
        <h3>Content Analysis</h3>
        
        {/* Copyright Information */}
        <div className="content-section">
          <h4>Copyright Information</h4>
          <div>
            <p><strong>Copyright Year Detected:</strong> {data.content.copyright.detected ? 'Yes' : 'No'}</p>
            
            {data.content.copyright.detected && (
              <>
                <p><strong>Year:</strong> {data.content.copyright.year}</p>
                <p><strong>Is Current:</strong> {data.content.copyright.is_current ? 'Yes' : 'No'}</p>
              </>
            )}
            
            {data.content.copyright.issues && data.content.copyright.issues.length > 0 && (
              <div className="issues">
                <h4>Issues:</h4>
                <ul className="issue-list">
                  {data.content.copyright.issues.map((issue, index) => (
                    <li key={index} className="issue-item">{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SEMrush Domain Overview Results */}
      {data.semrush && (
        <div className="result-section">
          <h3>SEMrush Domain Overview</h3>
          
          {data.semrush.error ? (
            <div className="error-message">
              <p>{data.semrush.error}</p>
            </div>
          ) : (
            <div>
              {data.semrush.data && (
                <div className="semrush-metrics" style={{display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px'}}>
                  <div className="metric-card" style={{flex: '1 0 200px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                    <h4 style={{margin: '0 0 15px 0', color: '#333'}}>Organic Traffic</h4>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Keywords</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          {data.semrush.data['Organic Keywords'] || '0'}
                        </p>
                      </div>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Traffic</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          {data.semrush.data['Organic Traffic'] || '0'}
                        </p>
                      </div>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Cost (USD)</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          ${data.semrush.data['Organic Cost'] || '0'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="metric-card" style={{flex: '1 0 200px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                    <h4 style={{margin: '0 0 15px 0', color: '#333'}}>Paid Advertising</h4>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Keywords</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          {data.semrush.data['Adwords Keywords'] || '0'}
                        </p>
                      </div>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Traffic</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          {data.semrush.data['Adwords Traffic'] || '0'}
                        </p>
                      </div>
                      <div>
                        <p style={{fontSize: '14px', color: '#666', margin: '5px 0'}}>Cost (USD)</p>
                        <p style={{fontSize: '24px', fontWeight: 'bold', margin: '0'}}>
                          ${data.semrush.data['Adwords Cost'] || '0'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{marginTop: '10px'}}>
                <p><strong>Domain Rank:</strong> {data.semrush.data['Rank'] || 'N/A'}</p>
                <p><strong>Database:</strong> {data.semrush.data['Database'] || 'us'}</p>
                <p style={{fontSize: '0.8em', color: '#666', marginTop: '15px'}}>
                  <em>Data provided by SEMrush API. Last updated: {data.semrush.timestamp || 'Unknown'}</em>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lighthouse Results */}
      {data.lighthouse && (
        <div className="result-section">
          <h3>Lighthouse Performance Analysis</h3>
          
          {data.lighthouse.error ? (
            <div className="error-message">
              <p>{data.lighthouse.error}</p>
            </div>
          ) : (
            <div>
              {/* Mobile/Desktop Tab Selector */}
              <div className="lighthouse-tabs" style={{marginBottom: '20px'}}>
                <button 
                  onClick={() => setActiveTab('mobile')}
                  style={{
                    padding: '8px 15px', 
                    marginRight: '10px', 
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: activeTab === 'mobile' ? '#007bff' : '#f0f0f0',
                    color: activeTab === 'mobile' ? 'white' : 'black',
                    fontWeight: activeTab === 'mobile' ? 'bold' : 'normal',
                    outline: 'none',
                    boxShadow: 'none'
                  }}
                >
                  Mobile
                </button>
                
                <button 
                  onClick={() => setActiveTab('desktop')}
                  style={{
                    padding: '8px 15px', 
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: activeTab === 'desktop' ? '#007bff' : '#f0f0f0',
                    color: activeTab === 'desktop' ? 'white' : 'black',
                    fontWeight: activeTab === 'desktop' ? 'bold' : 'normal',
                    outline: 'none',
                    boxShadow: 'none'
                  }}
                >
                  Desktop
                </button>
              </div>
              
              {/* Mobile Results */}
              <div className="tab-content mobile-results" style={{ display: activeTab === 'mobile' ? 'block' : 'none' }}>
                {data.lighthouse.mobile && data.lighthouse.mobile.success && (
                  <>
                    {/* Score Cards */}
                    <div className="score-cards" style={{display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '25px'}}>
                      <ScoreCard 
                        title="Performance" 
                        score={data.lighthouse.mobile.scores?.performance || 0} 
                        color="#0c6ef4" 
                      />
                      <ScoreCard 
                        title="Accessibility" 
                        score={data.lighthouse.mobile.scores?.accessibility || 0} 
                        color="#17ad66" 
                      />
                      <ScoreCard 
                        title="Best Practices" 
                        score={data.lighthouse.mobile.scores?.best_practices || 0} 
                        color="#8c44f7" 
                      />
                      <ScoreCard 
                        title="SEO" 
                        score={data.lighthouse.mobile.scores?.seo || 0} 
                        color="#ff5722" 
                      />
                    </div>
                    
                    {/* Core Web Vitals */}
                    <div className="core-vitals" style={{marginBottom: '25px'}}>
                      <h4>Core Web Vitals</h4>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px'}}>
                        <MetricCard 
                          name="Largest Contentful Paint" 
                          value={data.lighthouse.mobile.core_vitals?.largest_contentful_paint?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.largest_contentful_paint?.score || 0}
                        />
                        <MetricCard 
                          name="Total Blocking Time" 
                          value={data.lighthouse.mobile.core_vitals?.total_blocking_time?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.total_blocking_time?.score || 0}
                        />
                        <MetricCard 
                          name="Cumulative Layout Shift" 
                          value={data.lighthouse.mobile.core_vitals?.cumulative_layout_shift?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.cumulative_layout_shift?.score || 0}
                        />
                      </div>
                    </div>
                    
                    {/* Other Metrics */}
                    <div className="other-metrics" style={{marginBottom: '25px'}}>
                      <h4>Other Performance Metrics</h4>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px'}}>
                        <MetricCard 
                          name="First Contentful Paint" 
                          value={data.lighthouse.mobile.core_vitals?.first_contentful_paint?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.first_contentful_paint?.score || 0}
                        />
                        <MetricCard 
                          name="Speed Index" 
                          value={data.lighthouse.mobile.core_vitals?.speed_index?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.speed_index?.score || 0}
                        />
                        <MetricCard 
                          name="Time to Interactive" 
                          value={data.lighthouse.mobile.core_vitals?.time_to_interactive?.formatted || 'N/A'} 
                          score={data.lighthouse.mobile.core_vitals?.time_to_interactive?.score || 0}
                        />
                      </div>
                    </div>
                    
                    {/* Recommendations */}
                    {data.lighthouse.mobile.recommendations && (
                      <div className="recommendations">
                        <h4>Improvement Opportunities</h4>
                        
                        {/* Performance Recommendations */}
                        {data.lighthouse.mobile.recommendations.performance && 
                         data.lighthouse.mobile.recommendations.performance.length > 0 && (
                          <details style={{marginBottom: '15px'}}>
                            <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                              Performance Recommendations ({data.lighthouse.mobile.recommendations.performance.length})
                            </summary>
                            <ul style={{listStyleType: 'none', padding: 0}}>
                              {data.lighthouse.mobile.recommendations.performance.map((rec, idx) => (
                                <li key={idx} style={{
                                  marginBottom: '10px', 
                                  padding: '10px', 
                                  backgroundColor: '#f9f9f9', 
                                  borderLeft: `4px solid ${rec.impact === 'high' ? '#dc3545' : rec.impact === 'medium' ? '#ffc107' : '#6c757d'}`
                                }}>
                                  <strong>{rec.title}</strong>
                                  <p>{rec.description}</p>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        
                        {/* SEO Recommendations */}
                        {data.lighthouse.mobile.recommendations.seo && 
                         data.lighthouse.mobile.recommendations.seo.length > 0 && (
                          <details style={{marginBottom: '15px'}}>
                            <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                              SEO Recommendations ({data.lighthouse.mobile.recommendations.seo.length})
                            </summary>
                            <ul style={{listStyleType: 'none', padding: 0}}>
                              {data.lighthouse.mobile.recommendations.seo.map((rec, idx) => (
                                <li key={idx} style={{
                                  marginBottom: '10px', 
                                  padding: '10px', 
                                  backgroundColor: '#f9f9f9', 
                                  borderLeft: `4px solid ${rec.impact === 'high' ? '#dc3545' : rec.impact === 'medium' ? '#ffc107' : '#6c757d'}`
                                }}>
                                  <strong>{rec.title}</strong>
                                  <p>{rec.description}</p>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        
                        {/* Accessibility Recommendations */}
                        {data.lighthouse.mobile.recommendations.accessibility && 
                         data.lighthouse.mobile.recommendations.accessibility.length > 0 && (
                          <details style={{marginBottom: '15px'}}>
                            <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                              Accessibility Recommendations ({data.lighthouse.mobile.recommendations.accessibility.length})
                            </summary>
                            <ul style={{listStyleType: 'none', padding: 0}}>
                              {data.lighthouse.mobile.recommendations.accessibility.map((rec, idx) => (
                                <li key={idx} style={{
                                  marginBottom: '10px', 
                                  padding: '10px', 
                                  backgroundColor: '#f9f9f9', 
                                  borderLeft: `4px solid ${rec.impact === 'high' ? '#dc3545' : rec.impact === 'medium' ? '#ffc107' : '#6c757d'}`
                                }}>
                                  <strong>{rec.title}</strong>
                                  <p>{rec.description}</p>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Desktop Results */}
              <div className="tab-content desktop-results" style={{display: activeTab === 'desktop' ? 'block' : 'none'}}>
                {data.lighthouse.desktop && data.lighthouse.desktop.success && (
                  <>
                    {/* Score Cards */}
                    <div className="score-cards" style={{display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '25px'}}>
                      <ScoreCard 
                        title="Performance" 
                        score={data.lighthouse.desktop.scores?.performance || 0} 
                        color="#0c6ef4" 
                      />
                      <ScoreCard 
                        title="Accessibility" 
                        score={data.lighthouse.desktop.scores?.accessibility || 0} 
                        color="#17ad66" 
                      />
                      <ScoreCard 
                        title="Best Practices" 
                        score={data.lighthouse.desktop.scores?.best_practices || 0} 
                        color="#8c44f7" 
                      />
                      <ScoreCard 
                        title="SEO" 
                        score={data.lighthouse.desktop.scores?.seo || 0} 
                        color="#ff5722" 
                      />
                    </div>
                    
                    {/* Core Web Vitals */}
                    <div className="core-vitals" style={{marginBottom: '25px'}}>
                      <h4>Core Web Vitals</h4>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px'}}>
                        <MetricCard 
                          name="Largest Contentful Paint" 
                          value={data.lighthouse.desktop.core_vitals?.largest_contentful_paint?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.largest_contentful_paint?.score || 0}
                        />
                        <MetricCard 
                          name="Total Blocking Time" 
                          value={data.lighthouse.desktop.core_vitals?.total_blocking_time?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.total_blocking_time?.score || 0}
                        />
                        <MetricCard 
                          name="Cumulative Layout Shift" 
                          value={data.lighthouse.desktop.core_vitals?.cumulative_layout_shift?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.cumulative_layout_shift?.score || 0}
                        />
                      </div>
                    </div>

                    {/* Other Performance Metrics - Add to desktop view */}
                    <div className="other-metrics" style={{marginBottom: '25px'}}>
                      <h4>Other Performance Metrics</h4>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px'}}>
                        <MetricCard 
                          name="First Contentful Paint" 
                          value={data.lighthouse.desktop.core_vitals?.first_contentful_paint?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.first_contentful_paint?.score || 0}
                        />
                        <MetricCard 
                          name="Speed Index" 
                          value={data.lighthouse.desktop.core_vitals?.speed_index?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.speed_index?.score || 0}
                        />
                        <MetricCard 
                          name="Time to Interactive" 
                          value={data.lighthouse.desktop.core_vitals?.time_to_interactive?.formatted || 'N/A'} 
                          score={data.lighthouse.desktop.core_vitals?.time_to_interactive?.score || 0}
                        />
                      </div>
                    </div>

                    {/* Improvement Opportunities - Add to desktop view */}
                    <div className="improvement-opportunities" style={{marginBottom: '25px'}}>
                      <h4>Improvement Opportunities</h4>
                      
                      {/* Performance Recommendations */}
                      {data.lighthouse.desktop.recommendations?.performance && 
                       data.lighthouse.desktop.recommendations.performance.length > 0 && (
                        <details style={{marginBottom: '15px'}}>
                          <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                            Performance Recommendations ({data.lighthouse.desktop.recommendations.performance.length})
                          </summary>
                          <ul style={{listStyleType: 'none', padding: 0}}>
                            {data.lighthouse.desktop.recommendations.performance.map((rec, idx) => (
                              <li key={idx} style={{
                                marginBottom: '10px', 
                                padding: '10px', 
                                backgroundColor: '#f9f9f9', 
                                borderLeft: `4px solid ${rec.impact === 'high' ? '#dc3545' : rec.impact === 'medium' ? '#ffc107' : '#6c757d'}`
                              }}>
                                <strong>{rec.title}</strong>
                                <p>{rec.description}</p>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      
                      {/* Accessibility Recommendations */}
                      {data.lighthouse.desktop.recommendations?.accessibility && 
                       data.lighthouse.desktop.recommendations.accessibility.length > 0 && (
                        <details style={{marginBottom: '15px'}}>
                          <summary style={{cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px'}}>
                            Accessibility Recommendations ({data.lighthouse.desktop.recommendations.accessibility.length})
                          </summary>
                          <ul style={{listStyleType: 'none', padding: 0}}>
                            {data.lighthouse.desktop.recommendations.accessibility.map((rec, idx) => (
                              <li key={idx} style={{
                                marginBottom: '10px', 
                                padding: '10px', 
                                backgroundColor: '#f9f9f9', 
                                borderLeft: `4px solid ${rec.impact === 'high' ? '#dc3545' : rec.impact === 'medium' ? '#ffc107' : '#6c757d'}`
                              }}>
                                <strong>{rec.title}</strong>
                                <p>{rec.description}</p>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <p style={{fontSize: '0.8em', color: '#666', marginTop: '15px'}}>
                <em>Data provided by Lighthouse. Last updated: {data.lighthouse.timestamp || 'Unknown'}</em>
              </p>
              {/* CSS cleanup - tab switching now handled by React state */}
            </div>
          )}
        </div>
      )}

      {/* Summary of all issues */}
      <div className="result-section summary">
        <h3>Summary of Issues</h3>
        <ul className="summary-list">
          {data.wordpress.vulnerabilities && data.wordpress.vulnerabilities.length > 0 && (
            <li className="severity-high">
              WordPress has {data.wordpress.vulnerabilities.length} known vulnerabilities
            </li>
          )}
          
          {data.wordpress.is_wordpress && data.wordpress.theme && 
           data.wordpress.theme.name && !data.wordpress.theme.version && (
            <li className="severity-medium">
              WordPress theme "{data.wordpress.theme.name}" detected but version information is missing - consider updating theme
            </li>
          )}
          
          {data.ssl.issues && data.ssl.issues.length > 0 && (
            data.ssl.issues.map((issue, index) => (
              <li key={`ssl-${index}`} className="severity-medium">{issue}</li>
            ))
          )}
          
          {data.content.copyright.issues && data.content.copyright.issues.length > 0 && (
            data.content.copyright.issues.map((issue, index) => (
              <li key={`copyright-${index}`} className="severity-low">{issue}</li>
            ))
          )}
          
          {/* Broken Links Summary */}
          {data.links && data.links.broken_links && data.links.broken_links.length > 0 && (
            <li className="severity-medium">
              {data.links.broken_links.length} broken links detected
            </li>
          )}
          
          {/* Mixed Content Summary */}
          {data.links && data.links.mixed_content && data.links.mixed_content.has_mixed_content && (
            <li className="severity-high">
              Mixed content: HTTPS site loading resources over insecure HTTP
            </li>
          )}
          
          {data.security_headers && data.security_headers.issues && data.security_headers.issues.length > 0 && (
            data.security_headers.issues.map((issue, index) => (
              <li key={`security-${index}`} className="severity-medium">{issue}</li>
            ))
          )}
          
          {data.security_headers && data.security_headers.score < 50 && (
            <li className="severity-medium">
              Low security header score: {data.security_headers.score}% - Improve HTTP security headers
            </li>
          )}
          
          {(!data.wordpress.vulnerabilities || data.wordpress.vulnerabilities.length === 0) && 
           (!data.ssl.issues || data.ssl.issues.length === 0) && 
           (!data.content.copyright.issues || data.content.copyright.issues.length === 0) &&
           (!data.security_headers.issues || data.security_headers.issues.length === 0) && (
            <li className="no-issues">No major issues detected!</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default Results;
